export const CHECKOUT_URL = "https://implantcase-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/pSbu8jRF7vHBY1uo4VanYf/IMPLANTCASE-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/2zaXNNAh6Xz3U2v918niR9/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const beforeItems = [
  "You hesitate before accepting unfamiliar cases",
  "You review information without knowing what matters most",
  "You worry about overlooking an important risk factor",
  "You aren't always sure when a case needs additional preparation",
  "You sometimes accept cases outside your current comfort zone",
  "Every new implant case feels like a completely new problem",
];

export const afterItems = [
  "You follow the same evaluation sequence for every case",
  "You identify the critical variables before committing",
  "You classify the case as ACCEPT, PREPARE, or REFER",
  "You know what needs further evaluation before surgery",
  "You recognize when a case deserves additional expertise",
  "You build a more consistent decision-making process",
];

export const chairQuestions = [
  "Can I safely take this case?",
  "Am I missing something?",
  "Is this more complex than it looks?",
  "Should I prepare something first?",
  "Should I refer this patient?",
];

export const painPoints = [
  { icon: "user", text: "You have a patient who wants an implant, but you're not completely sure whether you should take the case yourself." },
  { icon: "scan", text: "You look at the clinical information and CBCT, but you're not always sure what deserves the most attention." },
  { icon: "help", text: "You find yourself asking, \"Is this actually a straightforward case?\"" },
  { icon: "clock", text: "You spend too much time reviewing notes, courses, or scattered information before making a decision." },
  { icon: "lock", text: "You know there are implant cases you could treat, but uncertainty keeps you from accepting them." },
  { icon: "alert", text: "You've had cases where something discovered later would have changed your original treatment plan." },
];

export const volumes = [
  {
    number: "I",
    title: "The Case Acceptance Filter",
    subtitle: "Know what you're looking at before you say YES",
    body: "The first layer of the decision process: the patient, clinical situation, periodontal status, relevant risk factors, and the variables that can change the complexity of an implant case. Organize the information before jumping into treatment.",
    points: [] as string[],
  },
  {
    number: "II",
    title: "The Anatomy & Risk Screen",
    subtitle: "Identify the variables that can change the case",
    body: "A structured way to review the anatomical situation before thinking about surgery. The goal isn't to memorize more anatomy — it's to know what to look for.",
    points: ["Available bone", "Height and width", "Relevant anatomical structures", "Risk zones", "Clinical limitations", "Variables needing more evaluation"],
  },
  {
    number: "III",
    title: "The CBCT Decision Map",
    subtitle: "Stop looking at the CBCT randomly",
    body: "A structured sequence to review the information that can influence your implant decision. Instead of staring at the scan wondering what you might be missing, you'll have a repeatable review process.",
    points: [] as string[],
  },
  {
    number: "IV",
    title: "The Implant Planning Checkpoint",
    subtitle: "Determine what must be resolved before surgery",
    body: "Organize everything that still needs an answer. The objective is simple: don't move forward with unanswered questions.",
    points: ["Implant position", "Implant axis", "Available space", "Surgical limitations", "Prosthetic considerations", "Safety margins", "Additional preparation", "Open questions"],
  },
  {
    number: "V",
    title: "Accept, Prepare or Refer",
    subtitle: "The decision framework for your final case assessment",
    body: "Everything comes together. Classify the case into three categories — without relying on gut feeling alone.",
    points: [] as string[],
  },
  {
    number: "VI",
    title: "The 15-Minute Case Decision Protocol™",
    subtitle: "Turn a complex case into a structured decision",
    body: "The complete workflow to reach a structured preliminary decision in 15 minutes or less once you've gathered the necessary clinical information — plus the complete case decision worksheet to use again and again.",
    points: [] as string[],
  },
];

export const decisionLevels = [
  { label: "ACCEPT", tone: "accept", text: "The case fits your current clinical scope and the relevant variables have been evaluated." },
  { label: "PREPARE", tone: "prepare", text: "The case may be treatable, but specific variables need to be addressed before proceeding." },
  { label: "REFER", tone: "refer", text: "The case requires expertise, complexity management, or treatment that makes referral the better option." },
];

export const protocolSteps = ["Patient", "Clinical data", "Anatomy", "CBCT", "Risk", "Planning", "Decision"];

export const tools = [
  { icon: "file", title: "The Implant Case Decision Worksheet", text: "A structured worksheet for reviewing each new case." },
  { icon: "check", title: "The Case Acceptance Checklist", text: "A quick-reference checklist so you don't overlook the major decision categories." },
  { icon: "filter", title: "The Anatomy Risk Filter", text: "A visual framework for identifying variables that may increase case complexity." },
  { icon: "grid", title: "The Accept / Prepare / Refer Matrix", text: "A simple decision matrix that brings the entire system together." },
  { icon: "search", title: "The Case Review Scorecard", text: "Document your assessment and identify areas that require further evaluation." },
  { icon: "folder", title: "The Clinical Case Summary Sheet", text: "Organize the relevant information before discussing a case with a colleague or specialist." },
];

export const bonuses = [
  {
    number: 1,
    title: "50 Implant Cases: Accept or Refer?",
    value: 47,
    text: "Fifty practice cases to train your clinical decision-making. Review the information, make your decision, then compare it with the structured reasoning provided.",
    image: "https://cdn.magicpatterns.com/uploads/s6AUaFeWbuEjghsma3Y9Fm/BONUS_1_IMPLANTCASE-EN.png",
  },
  {
    number: 2,
    title: "CBCT Red Flags™ Quick Guide",
    value: 37,
    text: "A visual reference with the key findings and warning signals to look for when reviewing an implant case. Keep it beside your workstation.",
    image: "https://cdn.magicpatterns.com/uploads/szS1Lz7T7ZtbS65Px2yggw/BONUS_2_IMPLANTCASE-EN.png",
  },
  {
    number: 3,
    title: "The 30 Most Common Implant Case Mistakes",
    value: 47,
    text: "The mistakes that happen when a case is accepted, planned, or evaluated without a structured process — and what to watch for before you commit.",
    image: "https://cdn.magicpatterns.com/uploads/3gmwZdZVmpbJ7ndXEX3xDG/BONUS_3_IMPLANTCASE-EN.png",
  },
  {
    number: 4,
    title: "The Implant Referral Decision Guide",
    value: 37,
    text: "A practical framework to identify when additional expertise, preparation, or referral should be considered. Knowing when not to take a case makes you a better clinician.",
    image: "https://cdn.magicpatterns.com/uploads/bT3bMBfcktRU3pyZjaArrW/BONUS_4_IMPLANT-CASE.png",
  },
  {
    number: 5,
    title: "The 30-Case Decision Checklist Pack",
    value: 29,
    text: "A collection of printable checklists and decision sheets to use while reviewing different implant cases.",
    image: "https://cdn.magicpatterns.com/uploads/j8XLY6tWkhfGPGTxP3NWWc/BONUS_5_IMPLANTCASE-EN.png",
  },
];

export const offerItems = [
  { title: "Implant Case Decision™ — 6 Premium Volumes", value: 97 },
  { title: "5 Exclusive Bonuses", value: 197 },
];

export const testimonials = [
  {
    name: "Dr. Daniel R.",
    country: "Colombia",
    headline: "I finally have a process for the cases that used to make me hesitate.",
    text: "Before this system, I would look at a case and immediately start thinking about the surgery. Now I force myself to go through the decision process first. It has completely changed how I approach unfamiliar cases.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/94122def-1eed-4211-b698-074f97136876.jpg",
  },
  {
    name: "Dr. Carlos M.",
    country: "Mexico",
    headline: "The Accept / Prepare / Refer framework is incredibly simple.",
    text: "What I liked most is that it doesn't try to replace clinical judgment. It gives you a structure for using it.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/94822ff6-64d2-4f34-986f-5231ad147977.jpg",
  },
  {
    name: "Dr. Andrés P.",
    country: "Venezuela",
    headline: "The CBCT section was the part I needed most.",
    text: "I used to look at a CBCT and worry that I was missing something. Having a defined sequence made the review much more organized.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/c9fe29e7-33ca-4ab2-aa7b-701b0cbd76bb.jpg",
  },
  {
    name: "Dr. James R.",
    country: "Spain",
    headline: "I wish I had this when I started placing implants.",
    text: "The biggest value for me is knowing what questions I need to answer before I commit to a case.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/30354b34-f2f4-467f-886a-f5a84a7ec01e.jpg",
  },
  {
    name: "Dr. Michael S.",
    country: "Argentina",
    headline: "This isn't another implantology PDF.",
    text: "It's a decision system. I can actually use the worksheets when I review a patient.",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/831e6158-805e-4f88-b5a3-2cab267b381f.jpg",
  },
];

export const faqs = [
  { q: "How will I receive the material?", a: "Immediately after your payment, you'll receive an email with access to the complete digital package, including all 6 volumes and 5 bonuses." },
  { q: "Is this a course?", a: "No. Implant Case Decision™ is a practical digital decision system with guides, checklists, matrices, worksheets, case exercises, and clinical reference materials." },
  { q: "Is this suitable for dentists who are beginning with implants?", a: "The system is designed especially for dentists who want a more structured approach to evaluating implant cases. However, it is not intended to replace formal clinical education, supervised training, professional judgment, or applicable local standards of care." },
  { q: "Does this tell me exactly which cases I should treat?", a: "No clinical system can make that decision for you. Implant Case Decision™ gives you a structured framework for reviewing the relevant variables and deciding whether a case appears appropriate for your current scope, requires additional preparation, or should be considered for referral." },
  { q: "Do I need to be an implant specialist?", a: "No. The framework is designed to help general dentists and clinicians working with implant cases organize their decision-making process." },
  { q: "Are the files downloadable?", a: "Yes. You'll receive downloadable digital files that you can save and access whenever you need them." },
  { q: "Is this a one-time payment?", a: "Yes. You pay $27 USD once. There are no monthly payments or recurring charges." },
  { q: "What happens if I don't like it?", a: "You have a 7-day money-back guarantee. Review the system, use the materials, and if it isn't right for you, contact us within 7 days for a full refund." },
];

export const recentBuyers = [
  { name: "Dr. Alejandro Gómez", country: "Mexico", flag: "🇲🇽" },
  { name: "Dr. Valentina Ríos", country: "Colombia", flag: "🇨🇴" },
  { name: "Dr. Martín Fernández", country: "Argentina", flag: "🇦🇷" },
  { name: "Dr. Camila Herrera", country: "Chile", flag: "🇨🇱" },
  { name: "Dr. Javier Morales", country: "Peru", flag: "🇵🇪" },
  { name: "Dr. Lucía Martínez", country: "Spain", flag: "🇪🇸" },
  { name: "Dr. Diego Castillo", country: "Ecuador", flag: "🇪🇨" },
  { name: "Dr. Sofía Ramírez", country: "Guatemala", flag: "🇬🇹" },
  { name: "Dr. Andrés Navarro", country: "Uruguay", flag: "🇺🇾" },
  { name: "Dr. Gabriela Torres", country: "Costa Rica", flag: "🇨🇷" },
  { name: "Dr. Ricardo Vargas", country: "Dominican Republic", flag: "🇩🇴" },
  { name: "Dr. Mariana López", country: "Panama", flag: "🇵🇦" },
  { name: "Dr. Fernando Silva", country: "Paraguay", flag: "🇵🇾" },
  { name: "Dr. Daniela Rojas", country: "Bolivia", flag: "🇧🇴" },
  { name: "Dr. José Mendoza", country: "Venezuela", flag: "🇻🇪" },
];
